import { useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

/**
 * Admin → user password reset modal.
 * Modes:
 *  - "link": email the user a reset link (uses the same machinery as /auth/forgot-password)
 *  - "force": set a new password directly
 */
export function PasswordResetDialog({ user, open, onClose }) {
  const [mode, setMode] = useState("link");
  const [pw, setPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [devLink, setDevLink] = useState("");

  const reset = () => {
    setMode("link");
    setPw("");
    setDevLink("");
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const submit = async () => {
    if (!user) return;
    if (mode === "force" && pw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/admin/users/${user.id}/reset-password`,
        mode === "force" ? { mode: "force", new_password: pw } : { mode: "link" },
      );
      if (mode === "link") {
        if (data.reset_link) {
          setDevLink(data.reset_link);
          toast.success("Reset link generated (dev preview)");
        } else {
          toast.success("Reset link sent");
          handleClose();
        }
      } else {
        toast.success(`New password set for ${user.email}`);
        handleClose();
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? handleClose() : null)}>
      <DialogContent data-testid="password-reset-dialog" className="rounded-none">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl tracking-tighter font-light">
            Reset password for {user.name}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#555]">
            {user.email}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Mode picker */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="reset-mode-link"
              onClick={() => setMode("link")}
              className={`label-eyebrow border px-4 py-3 text-left transition-colors ${
                mode === "link"
                  ? "border-[#1E3A8A] bg-[#F4EFE6]/40 text-[#1E3A8A]"
                  : "border-[#E5E0D8] text-[#555] hover:border-[#111]"
              }`}
            >
              Send reset link
            </button>
            <button
              type="button"
              data-testid="reset-mode-force"
              onClick={() => setMode("force")}
              className={`label-eyebrow border px-4 py-3 text-left transition-colors ${
                mode === "force"
                  ? "border-[#1E3A8A] bg-[#F4EFE6]/40 text-[#1E3A8A]"
                  : "border-[#E5E0D8] text-[#555] hover:border-[#111]"
              }`}
            >
              Set a new password
            </button>
          </div>

          {mode === "force" ? (
            <div>
              <label className="label-eyebrow text-[#555]">New password</label>
              <Input
                data-testid="reset-force-password"
                type="text"
                placeholder="At least 8 characters"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#111]"
              />
              <p className="text-xs text-[#888] mt-1 font-mono-aam">
                Share this password with the user securely. They can change it from their dashboard.
              </p>
            </div>
          ) : (
            <p className="text-sm text-[#555]">
              We&rsquo;ll email a one-time link to <span className="font-mono-aam text-[#1E3A8A]">{user.email}</span> that expires in one hour.
            </p>
          )}

          {devLink && (
            <div className="p-3 bg-[#F4EFE6]/40 border border-[#E5E0D8]" data-testid="reset-dev-link-block">
              <p className="label-eyebrow text-[#555]">— Dev/preview link</p>
              <a
                href={devLink}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono-aam text-xs text-[#1E3A8A] break-all underline mt-1 block"
              >
                {devLink}
              </a>
            </div>
          )}
        </div>

        <DialogFooter className="mt-2 gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            className="rounded-none border-[#E5E0D8] hover:bg-[#F4EFE6]/40 label-eyebrow"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="reset-submit-btn"
            onClick={submit}
            disabled={submitting || (mode === "force" && pw.length < 8)}
            className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white label-eyebrow disabled:opacity-50"
          >
            {submitting ? "Working…" : mode === "force" ? "Set password" : "Send link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PasswordResetDialog;
